import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react'
import { useCmsSections, useSaveCmsSection, useDeleteCmsSection, useEnsureCmsBlocks } from '@/hooks/data/useCrm'
import type { CmsSection } from '@/types/domain'
import CmsSectionModal, { type CmsSectionSubmitValues } from '@/components/cms/CmsSectionModal'
import { getKnownSectionSpec } from '@/components/cms/knownSections'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { InlineToggle } from '@/components/crud/InlineToggle'

/** The storefront pages a manager controls from here. `layout` is the site-wide header menu + footer. */
const PAGES: { key: string; label: string; help: string }[] = [
  { key: 'home', label: 'Home page', help: 'Every block on the home page. Switch a block off to hide it, use the arrows to reorder.' },
  { key: 'about', label: 'About page', help: 'The sections of the About us page. A hidden section is not shown at all.' },
  { key: 'contact', label: 'Contact page', help: 'Contact details, business hours, social media and the message form.' },
  { key: 'layout', label: 'Header & footer', help: 'The top menu links and the footer (links, text, social icons, copyright) on every page.' },
]

/** Blocks whose CONTENT lives in another admin module — here you can only show/hide/reorder them. */
const MANAGED_ELSEWHERE: Record<string, string> = {
  'hero-banner': 'Slides are managed in Hero slides.',
  'deals-banner': 'Shown while a campaign is ending — managed in Campaigns & offers.',
  campaigns: 'Managed in Campaigns & offers.',
  trending: 'Best-selling products, worked out from real orders.',
  'promo-tiles': 'Managed in Promo tiles.',
  brands: 'Built from the Brand field on your products.',
  'campaign-sections': 'Product sections created from Campaigns → homepage section.',
  merchandising: 'Featured / New / Best sellers — set on each product.',
  newsletter: 'Sign-ups appear under Newsletter.',
  social: 'Links are managed in Site Settings → Social Links (the footer uses the same ones).',
}

/** Old seed rows on the home page that no storefront block reads any more (the round categories, hero slides and featured products each have their own block above). */
const LEGACY_HOME = ['hero', 'features', 'featured-products', 'categories']

function CmsPage() {
  const [page, setPage] = useState('home')
  const sections = useCmsSections(page)
  const save = useSaveCmsSection()
  const del = useDeleteCmsSection()
  const ensure = useEnsureCmsBlocks()
  const confirmDelete = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<CmsSection | null>(null)
  const [moving, setMoving] = useState(false)

  // Opening a page makes sure every default block of it has a row, so each storefront section is listed here ready to hide or reorder.
  useEffect(() => {
    ensure.mutate(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const ordered = useMemo(() => [...(sections.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder), [sections.data])
  const pageInfo = PAGES.find((p) => p.key === page)!

  const submit = async (values: CmsSectionSubmitValues) => {
    await save.mutateAsync({
      id: values.id,
      page: values.page,
      key: values.key,
      title: values.title,
      contentJson: values.contentJson,
      sortOrder: values.sortOrder,
      published: values.published,
      backgroundColor: values.backgroundColor,
      textColor: values.textColor,
    })
  }

  /** Swap a block's position with its neighbour. Sort orders are re-spaced (10, 20, 30…) first so equal/legacy values can't tie. */
  const move = async (index: number, dir: -1 | 1) => {
    const other = index + dir
    if (other < 0 || other >= ordered.length) return
    setMoving(true)
    try {
      const next = [...ordered]
      ;[next[index], next[other]] = [next[other], next[index]]
      for (let i = 0; i < next.length; i++) {
        const wanted = (i + 1) * 10
        if (next[i].sortOrder !== wanted) await save.mutateAsync({ ...next[i], sortOrder: wanted })
      }
    } finally {
      setMoving(false)
    }
  }

  return (
    <div className="p-6">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Website (CMS)</h1>
        <button onClick={() => setCreating(true)} className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white">
          <Plus className="h-4 w-4" /> New Section
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {PAGES.map((p) => (
          <button
            key={p.key}
            onClick={() => setPage(p.key)}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
              page === p.key
                ? 'border-blue-600 bg-blue-600 text-white'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
        {pageInfo.help} Changes reach the live site within about a minute.
      </p>

      {sections.isLoading && <p className="text-sm text-gray-500">Loading sections…</p>}
      {sections.isError && <p className="text-sm text-red-600">Requires the manager role.</p>}

      <div className="space-y-3">
        {ordered.map((s, index) => {
          const spec = getKnownSectionSpec(page, s.key)
          const legacy = page === 'home' && LEGACY_HOME.includes(s.key)
          const elsewhere = legacy ? 'Old section — nothing on the storefront reads it any more. Safe to delete.' : MANAGED_ELSEWHERE[s.key]
          const hasFriendlyForm = !!spec
          const editable = hasFriendlyForm || legacy || elsewhere === undefined
          return (
            <div
              key={s.id}
              className={`rounded-lg border bg-white p-4 shadow-sm dark:bg-gray-900 ${
                s.published ? 'border-gray-100 dark:border-gray-800' : 'border-dashed border-gray-300 opacity-70 dark:border-gray-700'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <InlineToggle
                    checked={!!s.published}
                    onToggle={async (next) => {
                      await save.mutateAsync({ ...s, published: next })
                    }}
                  />
                  {(s.backgroundColor || s.textColor) && (
                    <span
                      className="h-6 w-6 shrink-0 rounded-full border border-gray-200"
                      style={{ backgroundColor: s.backgroundColor ?? undefined, color: s.textColor ?? undefined }}
                      title={`Background: ${s.backgroundColor ?? 'default'}, Text: ${s.textColor ?? 'default'}`}
                    >
                      Aa
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="truncate font-medium text-gray-900 dark:text-white">{s.title}</p>
                    <p className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
                      <span className="font-mono">{s.key}</span>
                      <span>·</span>
                      {s.published ? (
                        <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                          <Eye className="h-3 w-3" /> shown on the site
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                          <EyeOff className="h-3 w-3" /> hidden
                        </span>
                      )}
                    </p>
                    {elsewhere && <p className="mt-0.5 text-xs text-gray-400">{elsewhere}</p>}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    onClick={() => move(index, -1)}
                    disabled={index === 0 || moving}
                    className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800"
                    title="Move up"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => move(index, 1)}
                    disabled={index === ordered.length - 1 || moving}
                    className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800"
                    title="Move down"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  {editable && (
                    <button onClick={() => setEditing(s)} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" title="Edit content">
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() =>
                      confirmDelete(s.title, async () => {
                        await del.mutateAsync(s.id!)
                      })
                    }
                    className="rounded-lg p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                    title="Delete section (it is re-created with defaults next time this page is opened — use the switch to hide instead)"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )
        })}
        {sections.data && sections.data.length === 0 && !ensure.isPending && (
          <p className="text-sm text-gray-500">No sections on "{page}" yet.</p>
        )}
      </div>

      <CmsSectionModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        page={page}
        section={null}
        defaultSortOrder={(ordered[ordered.length - 1]?.sortOrder ?? 0) + 10}
        onSubmit={submit}
      />

      <CmsSectionModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        page={page}
        section={editing}
        defaultSortOrder={editing?.sortOrder ?? 0}
        onSubmit={submit}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/cms/')({ component: CmsPage })
